import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../utils/axios';
import {
  Card, Row, Col, Table, Tag, Space, Badge, Avatar,
  Statistic, Button, Tooltip, Progress
} from 'antd';
import {
  ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined,
  FileTextOutlined, UserOutlined, CarOutlined, HomeOutlined,
  ReloadOutlined, ThunderboltOutlined, TeamOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { hssColors, commonStyles } from '../config/theme';
import Footer from '../components/Footer';

const tiempoTranscurrido = (fechaISO) => {
  if (!fechaISO) return '—';
  const diff = Date.now() - new Date(fechaISO).getTime();
  const mins = Math.floor(diff / 60000);
  const hrs = Math.floor(mins / 60);
  const dias = Math.floor(hrs / 24);
  if (dias > 0) return `${dias}d ${hrs % 24}h`;
  if (hrs > 0) return `${hrs}h ${mins % 60}m`;
  return `${mins}m`;
};

const urgenciaColor = (fechaISO) => {
  const hrs = (Date.now() - new Date(fechaISO).getTime()) / 3600000;
  if (hrs > 48) return '#ff4d4f';
  if (hrs > 24) return '#faad14';
  return '#52c41a';
};

const hoy = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const estaSemana = () => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d;
};

const esteMes = () => {
  const d = new Date();
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
};

const DashboardOperativo = () => {
  const navigate = useNavigate();
  const [ordenes, setOrdenes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ultimaActualizacion, setUltimaActualizacion] = useState(null);
  const [tick, setTick] = useState(0);

  const cargarOrdenes = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get('/ordenes');
      if (Array.isArray(res.data)) setOrdenes(res.data);
      setUltimaActualizacion(new Date());
    } catch {
      // silencioso
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarOrdenes();
    const intervaloRefresh = setInterval(cargarOrdenes, 30000);
    const intervaloTick = setInterval(() => setTick(t => t + 1), 60000);
    return () => { clearInterval(intervaloRefresh); clearInterval(intervaloTick); };
  }, [cargarOrdenes]);

  // Derivaciones
  const activas = ordenes.filter(o => o.estado === 'ACTIVO');
  const completadasHoy = ordenes.filter(o =>
    o.estado === 'COMPLETADO' && new Date(o.fecha_actualizacion) >= hoy()
  );
  const completadasSemana = ordenes.filter(o =>
    o.estado === 'COMPLETADO' && new Date(o.fecha_actualizacion) >= estaSemana()
  );
  const canceladas = ordenes.filter(o => o.estado === 'CANCELADO');

  // Completadas recientes (últimas 8)
  const completadasRecientes = [...ordenes]
    .filter(o => o.estado === 'COMPLETADO')
    .sort((a, b) => new Date(b.fecha_actualizacion) - new Date(a.fecha_actualizacion))
    .slice(0, 8);

  // Agrupado por técnico (activas)
  const porTecnico = activas.reduce((acc, o) => {
    const nombre = o.responsable?.nombre || 'Sin asignar';
    if (!acc[nombre]) acc[nombre] = { nombre, ordenes: [] };
    acc[nombre].ordenes.push(o);
    return acc;
  }, {});

  const columnsActivas = [
    {
      title: '#', dataIndex: 'id', key: 'id', width: 60,
      render: id => <span style={{ fontWeight: 700, color: hssColors.primary }}>#{id}</span>,
    },
    {
      title: 'Tipo', dataIndex: 'tipo', key: 'tipo', width: 140,
      render: t => <Tag icon={<FileTextOutlined />} color={t === 'Instalacion' ? 'blue' : t === 'Desinstalacion' ? 'red' : 'orange'}>{t}</Tag>,
    },
    {
      title: 'Vehículo', key: 'vehiculo',
      render: (_, r) => r.vehiculo ? (
        <Space direction="vertical" size={0}>
          <Space size={4}>
            <CarOutlined style={{ color: hssColors.secondary, fontSize: 14 }} />
            <strong style={{ fontSize: 14 }}>{r.vehiculo.chapa}</strong>
          </Space>
          <span style={{ fontSize: 12, color: hssColors.textSecondary }}>{r.vehiculo.marca} {r.vehiculo.modelo}</span>
        </Space>
      ) : '—',
    },
    {
      title: 'Cliente', key: 'cliente',
      render: (_, r) => r.vehiculo?.cliente ? (
        <Space size={4}>
          <UserOutlined style={{ color: hssColors.info, fontSize: 14 }} />
          <span style={{ fontSize: 14 }}>{r.vehiculo.cliente.nombre} {r.vehiculo.cliente.apellido}</span>
        </Space>
      ) : '—',
    },
    {
      title: 'Técnico', key: 'responsable',
      render: (_, r) => r.responsable ? (
        <Space size={6}>
          <Avatar size={28} style={{ background: hssColors.success, fontSize: 13 }}>
            {r.responsable.nombre.charAt(0).toUpperCase()}
          </Avatar>
          <span style={{ fontSize: 14 }}>{r.responsable.nombre}</span>
        </Space>
      ) : <span style={{ color: hssColors.gray400 }}>Sin asignar</span>,
    },
    {
      title: 'Tiempo activo', key: 'tiempo', width: 120,
      render: (_, r) => {
        const color = urgenciaColor(r.fecha_creacion);
        return (
          <Tooltip title={`Creado: ${new Date(r.fecha_creacion).toLocaleString()}`}>
            <Space size={4}>
              <ClockCircleOutlined style={{ color, fontSize: 14 }} />
              <span style={{ fontWeight: 600, color, fontSize: 14 }}>
                {tiempoTranscurrido(r.fecha_creacion)}
              </span>
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: 'Observaciones', dataIndex: 'observaciones', key: 'obs',
      render: t => t ? <span style={{ fontSize: 13, color: hssColors.textSecondary }}>{t}</span>
                     : <span style={{ color: hssColors.gray400, fontSize: 13 }}>—</span>,
    },
  ];

  const columnsRecientes = [
    {
      title: '#', dataIndex: 'id', key: 'id', width: 60,
      render: id => <span style={{ fontWeight: 600, color: hssColors.primary }}>#{id}</span>,
    },
    {
      title: 'Tipo', dataIndex: 'tipo', key: 'tipo', width: 130,
      render: t => <Tag color={t === 'Instalacion' ? 'blue' : t === 'Desinstalacion' ? 'red' : 'orange'}>{t}</Tag>,
    },
    {
      title: 'Vehículo', key: 'vehiculo',
      render: (_, r) => r.vehiculo
        ? <Space size={4}><CarOutlined style={{ color: hssColors.secondary, fontSize: 14 }} /><span>{r.vehiculo.chapa} — {r.vehiculo.marca}</span></Space>
        : '—',
    },
    {
      title: 'Técnico', key: 'responsable',
      render: (_, r) => r.responsable?.nombre || '—',
    },
    {
      title: 'Completado', key: 'fecha',
      render: (_, r) => (
        <Space size={4}>
          <CheckCircleOutlined style={{ color: hssColors.success, fontSize: 14 }} />
          <span style={{ fontSize: 13 }}>{new Date(r.fecha_actualizacion).toLocaleString()}</span>
        </Space>
      ),
    },
  ];

  const ordenesEsteMes = ordenes.filter(o => new Date(o.fecha_creacion) >= esteMes());
  const completadasEsteMes = ordenesEsteMes.filter(o => o.estado === 'COMPLETADO');
  const pctCompletadas = ordenesEsteMes.length > 0
    ? Math.round((completadasEsteMes.length / ordenesEsteMes.length) * 100)
    : 0;

  return (
    <div style={{ minHeight: '100vh', background: hssColors.backgroundPrimary, display: 'flex', flexDirection: 'column' }}>
      <style>{`
        .orden-critica td { background-color: #fff1f0 !important; }
        .orden-alerta   td { background-color: #fffbe6 !important; }
      `}</style>
      {/* Header */}
      <div style={{ background: hssColors.backgroundSecondary, boxShadow: commonStyles.shadowMd, position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ padding: '16px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
            <img src="/logo-hss.png" alt="HSS" style={{ height: 45, cursor: 'pointer' }} onClick={() => navigate('/dashboard')} />
            <div style={{ borderLeft: `2px solid ${hssColors.gray200}`, height: 40 }} />
            <div>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: hssColors.primary }}>Dashboard Operativo</h1>
              <p style={{ margin: 0, fontSize: 13, color: hssColors.textSecondary }}>
                Monitoreo en tiempo real ·{' '}
                {ultimaActualizacion
                  ? `Actualizado ${ultimaActualizacion.toLocaleTimeString()}`
                  : 'Cargando...'}
              </p>
            </div>
          </div>
          <Space>
            <Tooltip title="Actualizar ahora">
              <Button icon={<ReloadOutlined />} loading={loading} onClick={cargarOrdenes} style={{ borderRadius: 8 }}>
                Actualizar
              </Button>
            </Tooltip>
            <Button type="default" icon={<HomeOutlined />} onClick={() => navigate('/dashboard')} size="large" style={{ height: 44, borderRadius: 8, fontWeight: 600 }}>
              Volver
            </Button>
          </Space>
        </div>
      </div>

      <div style={{ padding: '24px 32px', flex: 1 }}>

        {/* Stats cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd, borderLeft: `4px solid ${hssColors.warning}` }}>
              <Statistic
                title={<span style={{ color: hssColors.textSecondary, fontSize: 15 }}>Órdenes activas</span>}
                value={activas.length}
                prefix={<ThunderboltOutlined style={{ color: hssColors.warning }} />}
                valueStyle={{ color: hssColors.warning, fontWeight: 700 }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd, borderLeft: `4px solid ${hssColors.success}` }}>
              <Statistic
                title={<span style={{ color: hssColors.textSecondary, fontSize: 15 }}>Completadas hoy</span>}
                value={completadasHoy.length}
                prefix={<CheckCircleOutlined style={{ color: hssColors.success }} />}
                valueStyle={{ color: hssColors.success, fontWeight: 700 }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd, borderLeft: `4px solid ${hssColors.info}` }}>
              <Statistic
                title={<span style={{ color: hssColors.textSecondary, fontSize: 15 }}>Completadas esta semana</span>}
                value={completadasSemana.length}
                prefix={<CheckCircleOutlined style={{ color: hssColors.info }} />}
                valueStyle={{ color: hssColors.info, fontWeight: 700 }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd, borderLeft: `4px solid ${hssColors.error}` }}>
              <Statistic
                title={<span style={{ color: hssColors.textSecondary, fontSize: 15 }}>Canceladas</span>}
                value={canceladas.length}
                prefix={<CloseCircleOutlined style={{ color: hssColors.error }} />}
                valueStyle={{ color: hssColors.error, fontWeight: 700 }}
              />
            </Card>
          </Col>
        </Row>

        {/* Progreso semanal + Técnicos */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} lg={8}>
            <Card
              title={<Space><TeamOutlined style={{ color: hssColors.primary }} /><span>Técnicos activos</span></Space>}
              style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd, height: '100%' }}
              styles={{ body: { padding: '16px 20px' } }}
            >
              {Object.keys(porTecnico).length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px 0', color: hssColors.textSecondary }}>
                  No hay órdenes activas en este momento
                </div>
              ) : (
                Object.values(porTecnico).map(tec => (
                  <div key={tec.nombre} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 0', borderBottom: `1px solid ${hssColors.gray100}`
                  }}>
                    <Space>
                      <Avatar size={36} style={{ background: hssColors.primary, fontSize: 15 }}>
                        {tec.nombre.charAt(0).toUpperCase()}
                      </Avatar>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 15, color: hssColors.textPrimary }}>{tec.nombre}</div>
                        <div style={{ fontSize: 12, color: hssColors.textSecondary }}>
                          {tec.ordenes.map(o => `#${o.id}`).join(' · ')}
                        </div>
                      </div>
                    </Space>
                    <Badge
                      count={tec.ordenes.length}
                      style={{ background: hssColors.warning, fontWeight: 700 }}
                    />
                  </div>
                ))
              )}
            </Card>
          </Col>

          <Col xs={24} lg={16}>
            <Card
              title={<Space><CheckCircleOutlined style={{ color: hssColors.success }} /><span>Rendimiento semanal</span></Space>}
              style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd, height: '100%' }}
              styles={{ body: { padding: '24px' } }}
            >
              <Row gutter={[24, 24]}>
                <Col span={12}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 14, color: hssColors.textSecondary, marginBottom: 12 }}>Completadas este mes</div>
                    <Progress
                      type="circle"
                      percent={pctCompletadas}
                      strokeColor={hssColors.success}
                      size={120}
                      format={p => <span style={{ fontSize: 20, fontWeight: 700, color: hssColors.success }}>{p}%</span>}
                    />
                    <div style={{ marginTop: 8, fontSize: 13, color: hssColors.textSecondary }}>
                      {completadasEsteMes.length} de {ordenesEsteMes.length} órdenes del mes
                    </div>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16, justifyContent: 'center', height: '100%' }}>
                    {[
                      { label: 'Instalaciones', value: ordenesEsteMes.filter(o => o.tipo === 'Instalacion').length, color: '#3498db' },
                      { label: 'Desinstalaciones', value: ordenesEsteMes.filter(o => o.tipo === 'Desinstalacion').length, color: '#e74c3c' },
                      { label: 'Mantenimientos', value: ordenesEsteMes.filter(o => o.tipo === 'Mantenimiento').length, color: '#f39c12' },
                    ].map(item => (
                      <div key={item.label}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ fontSize: 14, color: hssColors.textSecondary }}>{item.label}</span>
                          <span style={{ fontWeight: 700, color: item.color }}>{item.value}</span>
                        </div>
                        <Progress
                          percent={ordenesEsteMes.length > 0 ? Math.round((item.value / ordenesEsteMes.length) * 100) : 0}
                          strokeColor={item.color}
                          showInfo={false}
                          size="small"
                        />
                      </div>
                    ))}
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        {/* Órdenes activas */}
        <Card
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Space>
                <ThunderboltOutlined style={{ color: hssColors.warning }} />
                <span style={{ fontWeight: 700 }}>En proceso ahora</span>
                <Badge count={activas.length} style={{ background: hssColors.warning }} />
              </Space>
              <span style={{ fontSize: 13, color: hssColors.textSecondary, fontWeight: 400 }}>
                🔴 &gt;48h · 🟡 &gt;24h · 🟢 Normal
              </span>
            </div>
          }
          style={{ marginBottom: 24, borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }}
          styles={{ body: { padding: 0 } }}
        >
          <Table
            columns={columnsActivas}
            dataSource={activas.sort((a, b) => new Date(a.fecha_creacion) - new Date(b.fecha_creacion))}
            rowKey="id"
            loading={loading}
            pagination={false}
            locale={{ emptyText: 'No hay órdenes activas en este momento' }}
            style={{ borderRadius: 12, overflow: 'hidden' }}
            rowClassName={(record) => {
              const hrs = (Date.now() - new Date(record.fecha_creacion).getTime()) / 3600000;
              if (hrs > 48) return 'orden-critica';
              if (hrs > 24) return 'orden-alerta';
              return '';
            }}
          />
        </Card>

        {/* Últimas completadas */}
        <Card
          title={
            <Space>
              <CheckCircleOutlined style={{ color: hssColors.success }} />
              <span style={{ fontWeight: 700 }}>Últimas completadas</span>
            </Space>
          }
          style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }}
          styles={{ body: { padding: 0 } }}
        >
          <Table
            columns={columnsRecientes}
            dataSource={completadasRecientes}
            rowKey="id"
            pagination={false}
            locale={{ emptyText: 'Sin órdenes completadas aún' }}
            style={{ borderRadius: 12, overflow: 'hidden' }}
          />
        </Card>

      </div>
      <Footer />
    </div>
  );
};

export default DashboardOperativo;
